"use client";

import { useEffect } from "react";

/** After a deploy, an already-open tab may lazy-load a chunk whose hash no longer
 *  exists on the server → ChunkLoadError / "Failed to load chunk". Instead of a dead
 *  screen, reload once to pull the fresh bundle. The sessionStorage guard blocks an
 *  infinite reload loop when a chunk is genuinely gone (network down, not a stale build). */
const GUARD_KEY = "cg-chunk-reload";
const COOLDOWN_MS = 10_000;

function isChunkError(text?: string | null): boolean {
  if (!text) return false;
  return (
    text.includes("ChunkLoadError") ||
    text.includes("Loading chunk") ||
    text.includes("Loading CSS chunk") ||
    text.includes("Failed to load chunk")
  );
}

function recover(): void {
  const last = Number(sessionStorage.getItem(GUARD_KEY) || 0);
  if (Date.now() - last < COOLDOWN_MS) return; // already reloaded once — don't loop
  sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  window.location.reload();
}

/** Mounts window-level listeners; renders nothing. */
export function ChunkReloader(): null {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      const el = e.target as HTMLScriptElement | HTMLLinkElement | null;
      const src = (el as HTMLScriptElement)?.src || (el as HTMLLinkElement)?.href || "";
      const isChunkAsset =
        src.includes("/_next/static/") && (el?.tagName === "SCRIPT" || el?.tagName === "LINK");
      if (isChunkError(e.message) || isChunkError(e.error?.name) || isChunkAsset) recover();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      if (isChunkError(r?.name) || isChunkError(r?.message) || isChunkError(String(r))) recover();
    };
    window.addEventListener("error", onError, true); // capture — catches resource-load errors too
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}
